import { useEffect, useState, useCallback } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { Panel, Field, inputCls, CCButton, EmptyState, LoadingState, StatusBadge } from "@/command/ccUi";

const TYPES = ["Budget", "Expense", "Hiring", "Vendor", "Purchase Order", "Contract", "General"];
const PRIOS = ["Low", "Medium", "High", "Critical"];
const EMPTY = { title: "", type: "General", description: "", amount: "", priority: "Medium", related: "" };

export default function Approvals() {
  const { can } = useCommand();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("Pending");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const canApprove = can("approvals", "APPROVE");
  const canCreate = can("approvals", "CREATE");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/cc/approvals", { params: filter === "All" ? {} : { status: filter } })
      .then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useRealtime(["approvals"], load);

  const create = async () => {
    try {
      await api.post("/cc/approvals", { ...creating, amount: creating.amount ? Number(creating.amount) : null });
      toast.success("Approval requested"); setCreating(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const decide = async (id, decision) => {
    try { await api.post(`/cc/approvals/${id}/decision`, { decision }); toast.success("Decision recorded"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="display text-2xl md:text-3xl font-light text-ink">Approvals</h1>
      <p className="text-subtle mt-1 mb-6">Needs your decision.</p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {["Pending", "Approved", "Rejected", "Changes Requested", "All"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`appr-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${filter === s ? "bg-invert text-invert-ink" : "glass-medium text-subtle hover:text-ink"}`}>{s}</button>
        ))}
        {canCreate && <CCButton onClick={() => setCreating({ ...EMPTY })} testid="appr-new" className="ml-auto"><Plus className="w-4 h-4" /> New request</CCButton>}
      </div>

      {loading ? <LoadingState /> : rows.length === 0 ? <EmptyState title="No approvals here" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((a) => (
            <Panel key={a.id} testid={`approval-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={a.status} />
                    <span className="text-xs text-subtle">{a.type} · {a.priority}</span>
                  </div>
                  <h3 className="display text-lg font-medium text-ink mt-2">{a.title}</h3>
                  {a.description && <p className="text-sm text-subtle mt-1">{a.description}</p>}
                </div>
                {a.amount != null && <span className="display text-lg text-ink">₹{Number(a.amount).toLocaleString()}</span>}
              </div>
              <p className="text-[11px] text-subtle mt-3">Requested by {a.requester} · {new Date(a.created_at).toLocaleDateString()}</p>
              {a.status === "Pending" && canApprove && (
                <div className="flex gap-2 mt-4">
                  <CCButton onClick={() => decide(a.id, "approve")} testid={`approve-${a.id}`}>Approve</CCButton>
                  <CCButton variant="danger" onClick={() => decide(a.id, "reject")} testid={`reject-${a.id}`}>Reject</CCButton>
                  <CCButton variant="ghost" onClick={() => decide(a.id, "changes")} testid={`changes-${a.id}`}>Request changes</CCButton>
                </div>
              )}
              {a.status !== "Pending" && a.decided_by && <p className="text-xs text-subtle mt-3">{a.status} by {a.decided_by}</p>}
            </Panel>
          ))}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setCreating(null)} data-testid="appr-modal">
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">New approval request</h3>
            <div className="flex flex-col gap-4">
              <Field label="Title"><input className={inputCls} value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} data-testid="appr-title" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Type"><select className={inputCls} value={creating.type} onChange={(e) => setCreating({ ...creating, type: e.target.value })} data-testid="appr-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
                <Field label="Priority"><select className={inputCls} value={creating.priority} onChange={(e) => setCreating({ ...creating, priority: e.target.value })} data-testid="appr-priority">{PRIOS.map((t) => <option key={t}>{t}</option>)}</select></Field>
              </div>
              <Field label="Amount (optional)"><input type="number" className={inputCls} value={creating.amount} onChange={(e) => setCreating({ ...creating, amount: e.target.value })} data-testid="appr-amount" /></Field>
              <Field label="Description"><textarea className={`${inputCls} resize-y`} rows={3} value={creating.description} onChange={(e) => setCreating({ ...creating, description: e.target.value })} data-testid="appr-desc" /></Field>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setCreating(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button>
              <CCButton onClick={create} testid="appr-save">Submit request</CCButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
